import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

/**
 * Handles qty and variant select changes on the cart page line items.
 *
 * Qty selects (.cart-items__qty-select) delegate to the cart-items-component's
 * existing updateQuantity method so Horizon's morph + event pipeline runs as normal.
 *
 * Variant selects (.cart-items__option-select) do a two-step remove + add via the
 * Shopify Cart API, then morph the cart section with the final re-rendered HTML.
 */

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLSelectElement)) return;

  if (target.classList.contains('cart-items__qty-select')) {
    handleQtyChange(target);
  } else if (target.classList.contains('cart-items__option-select')) {
    handleVariantChange(target);
  }
});

/**
 * @param {HTMLSelectElement} select
 */
function handleQtyChange(select) {
  const line = parseInt(select.dataset.line ?? '0', 10);
  const quantity = parseInt(select.value, 10);
  /** @type {any} */
  const cartItemsComponent = select.closest('cart-items-component');
  if (!cartItemsComponent) return;

  if (quantity === 0) {
    // onLineItemRemove already applies the loading/spinner state itself.
    cartItemsComponent.onLineItemRemove(line);
    return;
  }

  // Show the same in-flight loading state as a removal (blur + centred spinner)
  // while the quantity update is running. On success the section morph re-renders
  // the row and drops the class; only clear it manually if the request fails.
  const row = select.closest('.cart-items__table-row');
  row?.classList.add('cart-items__table-row--removing');

  const result = cartItemsComponent.updateQuantity({ line, quantity, action: 'change' });
  if (result && typeof result.then === 'function') {
    result.catch(() => {
      row?.classList.remove('cart-items__table-row--removing');
    });
  }
}

/**
 * @param {HTMLSelectElement} select
 */
async function handleVariantChange(select) {
  const newVariantId = select.value;
  const itemKey = select.dataset.itemKey ?? '';
  const currentQty = parseInt(select.dataset.currentQty ?? '1', 10);
  // Original variant id, captured so a failed add (after the remove has already
  // gone through) can be rolled back instead of silently dropping the line.
  const originalVariantId = select.dataset.currentVariant ?? '';
  // The swap is a remove + re-add, so line item properties (e.g. gift card recipient details)
  // must be carried over or the new line is added without them.
  const properties = parseProperties(select.dataset.properties);
  /** @type {any} */
  const cartItemsComponent = select.closest('cart-items-component');
  if (!cartItemsComponent) return;

  const sectionId = cartItemsComponent.dataset.sectionId ?? '';

  // Capture enough to relocate this control after the section morph replaces the
  // row's markup (the row's id/data-item-key change because item.key is derived
  // from the new variant). data-line (cart position) plus the option's aria-label
  // (e.g. "Size") are the closest thing to a stable identity across the morph.
  const line = select.dataset.line ?? '';
  const optionLabel = select.getAttribute('aria-label') ?? '';
  const hadFocus = document.activeElement === select;

  // Disable all selects in this row while updating to prevent double-submit
  const row = select.closest('.cart-items__table-row');
  const rowSelects = /** @type {HTMLSelectElement[]} */ (row ? [...row.querySelectorAll('select')] : []);
  rowSelects.forEach((s) => {
    s.disabled = true;
  });

  // Tracks whether Step 1 has already mutated the cart, so a Step 2 failure can be
  // rolled back rather than leaving the shopper silently short a line item.
  let removed = false;

  try {
    // Step 1: remove old variant by item key (no sections — final state comes from the add)
    const removeResponse = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', {
        body: JSON.stringify({ id: itemKey, quantity: 0 }),
      })
    );

    if (!removeResponse.ok) throw new Error(`Cart remove failed: ${removeResponse.status}`);
    removed = true;

    // Step 2: add new variant and request a fresh re-render of the cart section
    const addResponse = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: parseInt(newVariantId, 10), quantity: currentQty, properties }],
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      })
    );

    if (!addResponse.ok) throw new Error(`Cart add failed: ${addResponse.status}`);

    const addData = await addResponse.json();
    const sectionHtml = /** @type {string | undefined} */ (addData.sections?.[sectionId]);

    if (sectionHtml) {
      await morphSection(sectionId, sectionHtml, { mode: 'full' });
      if (hadFocus) restoreVariantSelectFocus(cartItemsComponent, line, optionLabel);
    } else {
      window.location.reload();
    }
  } catch (error) {
    console.error('[cart-inline-selects] Variant change failed:', error);

    if (removed) {
      // The old line was already removed but the swap didn't complete, so the cart
      // is now missing that item. Try to re-add the original variant to undo the
      // removal, then reload so the UI reflects the true server-side cart state
      // (whether the rollback succeeded or not) — never leave the shopper silently
      // short an item with a stale row on screen.
      if (originalVariantId) {
        try {
          await fetch(
            Theme.routes.cart_add_url,
            fetchConfig('json', {
              body: JSON.stringify({
                items: [{ id: parseInt(originalVariantId, 10), quantity: currentQty, properties }],
              }),
            })
          );
        } catch (rollbackError) {
          console.error('[cart-inline-selects] Rollback re-add failed:', rollbackError);
        }
      }
      window.location.reload();
      return;
    }

    // Nothing was mutated (Step 1 itself failed) — just re-enable the selects to retry.
    rowSelects.forEach((s) => {
      s.disabled = false;
    });
  }
}

/**
 * Liquid's `item.properties | json` renders an array of [name, value] pairs, while the Cart API
 * expects an object, so convert it.
 * @param {string | undefined} json - The line item's properties, as rendered into data-properties.
 * @returns {Record<string, string>}
 */
function parseProperties(json) {
  if (!json) return {};

  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) return Object.fromEntries(parsed);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Restores focus after a variant change morphs the row's markup. The previous
 * select's row id and data-item-key are gone (item.key is derived from the new
 * variant), so the same cart line's option select — matched by its aria-label
 * where more than one option exists on the row — is the closest available
 * proxy for "the control the shopper was just using". Fails silently if the
 * cart line shifted (e.g. merged into an existing line) and no match is found.
 * @param {Element} cartItemsComponent - The cart-items-component the change happened in.
 * @param {string} line - The 1-based cart line captured before the morph.
 * @param {string} optionLabel - The option's aria-label (e.g. "Size") captured before the morph.
 * @returns {void}
 */
function restoreVariantSelectFocus(cartItemsComponent, line, optionLabel) {
  if (!line) return;

  const lineSelects = /** @type {HTMLSelectElement[]} */ ([
    ...cartItemsComponent.querySelectorAll(`select.cart-items__option-select[data-line="${line}"]`),
  ]);

  const match = lineSelects.find((s) => s.getAttribute('aria-label') === optionLabel) ?? lineSelects[0];

  match?.focus();
}
